import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useCreateSubscriptionsFromOrders,
  usePreviewCreateSubscriptionsFromOrders,
  type CreateFromOrdersOutput,
} from '@/lib/api/subscriptions';

import { ConfigureStep } from './configure-step';
import { ResultsStep } from './results-step';
import { ReviewStep } from './review-step';

type Step = 'configure' | 'review' | 'results';

interface CreateSubscriptionDialogProps {
  /** Single order and bulk selection are the same code path. */
  orderIds: string[];
  storeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateSubscriptionDialog({
  orderIds,
  storeId,
  open,
  onOpenChange,
}: CreateSubscriptionDialogProps) {
  const { t } = useTranslation('subscriptions');
  const [step, setStep] = React.useState<Step>('configure');
  const [priceId, setPriceId] = React.useState<string>();
  const [firstChargeDate, setFirstChargeDate] = React.useState<Date>();
  const [reason, setReason] = React.useState('');
  const [results, setResults] = React.useState<CreateFromOrdersOutput>([]);

  const billingCycleAnchor = firstChargeDate?.getTime();

  const { data: preview, isLoading: isPreviewLoading } = usePreviewCreateSubscriptionsFromOrders(
    { orderIds, priceId: priceId ?? '', billingCycleAnchor },
    step === 'review' && !!priceId,
  );

  const createMutation = useCreateSubscriptionsFromOrders();

  const resetState = () => {
    setStep('configure');
    setPriceId(undefined);
    setFirstChargeDate(undefined);
    setReason('');
    setResults([]);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      if (createMutation.isPending) return;
      resetState();
    }
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    if (!priceId) return;
    const created = await createMutation.mutateAsync({
      orderIds,
      priceId,
      billingCycleAnchor,
      reason: reason.trim(),
    });
    setResults(created);
    setStep('results');
  };

  const canReview = !!priceId && reason.trim().length > 0;
  const canConfirm = !!preview && preview.accepted.length > 0 && !createMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('createFromOrders.title')}
          </DialogTitle>
          <DialogDescription>
            {t('createFromOrders.description')}
          </DialogDescription>
        </DialogHeader>

        {step === 'configure' ? (
          <ConfigureStep
            storeId={storeId}
            orderCount={orderIds.length}
            priceId={priceId}
            firstChargeDate={firstChargeDate}
            reason={reason}
            onPriceChange={setPriceId}
            onFirstChargeDateChange={setFirstChargeDate}
            onReasonChange={setReason}
          />
        ) : null}

        {step === 'review' ? <ReviewStep preview={preview} isLoading={isPreviewLoading} /> : null}

        {step === 'results' ? <ResultsStep results={results} /> : null}

        <DialogFooter>
          {step === 'configure' ? (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                {t('createFromOrders.cancel')}
              </Button>
              <Button disabled={!canReview} onClick={() => setStep('review')}>
                {t('createFromOrders.continue')}
              </Button>
            </>
          ) : null}

          {step === 'review' ? (
            <>
              <Button variant="outline" onClick={() => setStep('configure')}>
                {t('createFromOrders.back')}
              </Button>
              <Button disabled={!canConfirm} onClick={handleConfirm}>
                {createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t('createFromOrders.confirm')}
              </Button>
            </>
          ) : null}

          {step === 'results' ? (
            <Button onClick={() => handleOpenChange(false)}>
              {t('createFromOrders.done')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
